/**
 * Dual pricing (SCOPE §8.2, §16-6).
 *
 * **It is not a surcharge.** Every item in the store carries two prices: a cash
 * price and a card price. Inventory is entered at the cash price and the card
 * price is derived from the tenant's configured program rate. A customer paying
 * cash gets the cash price; a customer paying card gets the card price. That
 * framing — two posted prices, not a fee bolted on at the end — is the whole
 * compliance basis of the program, and it is why nothing in this module ever
 * produces a line item called "fee".
 *
 * The consequence that matters for the register: **on a split tender, each
 * portion is priced by how it is paid.** A $100 cash-price sale at a 3% program
 * rate is $103 all-card. Pay $50 in cash and the other half is card-priced:
 * $50 x 1.03 = $51.50, for $101.50 collected. The customer really did buy half
 * the basket at the cash price and half at the card price.
 *
 * Because tax is part of the price the customer pays, the same proportion
 * applies to it. Uplifting the whole cash total (goods + tax) is arithmetically
 * identical to taxing the card prices, so the receipt can honestly present it
 * either way: "$37.84 tax, $1.32 card-price adjustment".
 */

import type { Cents } from '../money/index';
import { ZERO_CENTS, centsFromBigInt, divideRounded } from '../money/index';
import type { DualPricingConfig } from './types';

/**
 * Scale `amount` by the sale's card/cash ratio.
 *
 * Deliberately expressed as a ratio of the two totals rather than as
 * `x (1 + rate)`. The totals are built bottom-up from per-line card prices
 * (SCOPE §18.1-1, so receipts foot), which makes their ratio the sale's true
 * effective rate. Using it here means the two endpoints are exact: paying
 * nothing in cash charges exactly `cardTotal`, and paying everything in cash
 * charges exactly `cashTotal`. No cent appears or disappears at the extremes,
 * which is where a customer would actually notice.
 */
function scaleBetweenTotals(amount: Cents, from: Cents, to: Cents): Cents {
  if (amount === 0 || from === 0) return ZERO_CENTS;
  if (from === to) return amount;
  return centsFromBigInt(divideRounded(BigInt(amount) * BigInt(to), BigInt(from)));
}

/**
 * What to charge on a card to clear `cashRemaining` of the basket.
 *
 * `cashRemaining` is the outstanding balance measured in cash prices; the
 * return value is what the terminal is asked to authorise.
 */
export function cardAmountDue(cashRemaining: Cents, cashTotal: Cents, cardTotal: Cents): Cents {
  return scaleBetweenTotals(cashRemaining, cashTotal, cardTotal);
}

/**
 * The inverse: how much cash-priced basket a card payment of `cardAmount`
 * covers. Used when a customer splits across two cards and names an amount on
 * the first one.
 */
export function cashEquivalentOfCardAmount(
  cardAmount: Cents,
  cashTotal: Cents,
  cardTotal: Cents,
): Cents {
  return scaleBetweenTotals(cardAmount, cardTotal, cashTotal);
}

/** The card price of a single cash amount — a line, or a whole sale. */
export function cardPriceOf(cashAmount: Cents, config: DualPricingConfig): Cents {
  if (!config.enabled) return cashAmount;
  return centsFromBigInt(
    divideRounded(BigInt(cashAmount) * BigInt(1_000_000 + config.ratePpm), 1_000_000n),
  );
}

export type { DualPricingConfig };
